import { Link } from "react-router-dom";
import { signInURL } from "../api";
import { useT } from "../i18n";

/**
 * PlusOffer is the Get Plus screen's offer (requirement 0125, ADR-0193): what
 * Plus gives and one Get Plus button. Shown wherever the server refuses a
 * read with plus_required — the book page of a locked book, a chapter opened
 * by link — instead of an error.
 *
 * **No price on the button**, or anywhere on this card: the price is asked
 * about, after the tap, never offered (Apple 3.1.1, Play payments policy).
 *
 * The button is a link, not a fetch: the question screen's own fetch is the
 * counted tap (ADR-0190 §5), so nothing here may prefetch it.
 */
export default function PlusOffer({
  bookID,
  book,
  then,
  signInFirst = false,
}: {
  bookID?: string;
  /** The book tapped, when known: the offer says what this one has. */
  book?: { listen_ms?: number; multi_speaker?: boolean };
  then?: string;
  /** A visitor (ADR-0197): Plus is an account's, so the button signs in first
   *  and comes back to Get Plus. */
  signInFirst?: boolean;
}) {
  const t = useT();
  const params = new URLSearchParams();
  if (bookID) params.set("book", bookID);
  if (then) params.set("then", then);
  const query = params.toString();
  return (
    <section
      data-testid="plus-offer"
      className="mt-2 w-full max-w-md rounded-card border border-sage/40 bg-sage-soft/40 px-5 py-4 text-start"
    >
      <h2 className="text-base font-semibold">{t.plus.offerTitle}</h2>
      <p className="mt-1 text-sm text-ink-soft">{t.plus.offerBody}</p>
      <ul className="mt-2 list-disc space-y-0.5 ps-5 text-sm text-ink-soft">
        {t.plus
          .benefits(book && { narrated: (book.listen_ms ?? 0) > 0, voices: !!book.multi_speaker })
          .map((b) => (
            <li key={b}>{b}</li>
          ))}
      </ul>
      {signInFirst ? (
        <a
          href={signInURL(`/plus${query ? `?${query}` : ""}`)}
          data-testid="get-plus"
          className={button}
        >
          {t.plus.signInForPlus}
        </a>
      ) : (
        <Link to={`/plus${query ? `?${query}` : ""}`} data-testid="get-plus" className={button}>
          {t.plus.getPlus}
        </Link>
      )}
    </section>
  );
}

const button =
  "mt-4 inline-flex rounded-full bg-sage px-5 py-2 text-[0.95rem] font-semibold text-on-sage shadow-[0_10px_24px_-12px_var(--c-sage-deep)] transition-transform active:scale-[0.98]";
